import React, { useMemo, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { CheckCircle2, Sparkles, ArrowRight, PartyPopper } from 'lucide-react'
import { getSession } from '../lib/session'
import { byKey } from '../lib/serviceCatalog'
import ContentSource from '../components/ContentSource'

// Where a fresh signup lands right after checkout (Stripe success_url and the
// test-mode fallback both point here). One job: get their first content in so
// the thing they just paid for actually works — with a clear "Skip for now".
export default function Onboarding() {
  const session = getSession()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [added, setAdded] = useState(0)

  const services = useMemo(() => {
    const keys = (params.get('activated') || '').split(',').map(s => s.trim()).filter(Boolean)
    return keys.map(byKey).filter(Boolean)
  }, [params])

  // Do any of the purchased services actually read the shared knowledge base?
  const contentServices = services.filter(s => s.needsContent && !s.ownContent)
  const ragOnly = services.length > 0 && services.every(s => s.ownContent || !s.needsContent)

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-green-500/10 mb-3">
          <PartyPopper className="text-green-600" size={26} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">You’re all set{session?.namespaceId ? '' : ''} 🎉</h2>
        <p className="text-gray-500 mt-1">
          {services.length
            ? <>Your {services.length === 1 ? 'service is' : `${services.length} services are`} active. Now give your workspace something to work with.</>
            : <>Your workspace is ready. Let’s give it some content to work with.</>}
        </p>
      </div>

      {/* What you just activated */}
      {services.length > 0 && (
        <div className="card mt-6">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">Active on your workspace</p>
          <div className="space-y-3">
            {services.map(s => {
              const Icon = s.icon
              return (
                <div key={s.key} className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary-500/10 shrink-0"><Icon className="text-primary-600" size={16} /></div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                      {s.name} <CheckCircle2 size={14} className="text-green-500" />
                    </p>
                    <p className="text-xs text-gray-500">{s.onboard || s.short}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* The content step — only if something they bought reads the shared KB */}
      {(contentServices.length > 0 || services.length === 0) && (
        <div className="card mt-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Sparkles size={18} className="text-primary-600" /> Add your content
          </h3>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            A website URL is just one option — feed it however your knowledge actually lives: paste text, upload a
            document, or connect a database, cloud bucket, or Confluence/Jira. Everything you add here is shared across
            {contentServices.length ? ` your ${contentServices.map(s => s.name).join(', ')}.` : ' your services.'}
          </p>
          <ContentSource onDone={() => setAdded(n => n + 1)} />
          {added > 0 && (
            <p className="text-sm text-green-600 mt-3 flex items-center gap-1.5">
              <CheckCircle2 size={15} /> {added} source{added > 1 ? 's' : ''} added — indexing in the background. Add more, or head to your workspace.
            </p>
          )}
        </div>
      )}

      {/* RAG-only note (it uses its own collections, not the shared KB) */}
      {ragOnly && services.some(s => s.key === 'rag') && (
        <div className="card mt-6 bg-primary-500/5 border-primary-500/20">
          <p className="text-sm text-gray-700">
            <b>Custom RAG</b> organises content into tuneable collections rather than one shared knowledge base.
            Open RAG, create your first collection, then add documents to it.
          </p>
          <Link to="/rag" className="btn-primary inline-flex items-center gap-2 mt-3">Open RAG <ArrowRight size={15} /></Link>
        </div>
      )}

      {/* Always an escape hatch */}
      <div className="flex items-center justify-between mt-6">
        <button onClick={() => navigate('/')} className="text-sm text-gray-500 hover:text-gray-900">
          Skip for now — I’ll add content later
        </button>
        <button onClick={() => navigate('/')} className="btn-primary flex items-center gap-2">
          Go to my workspace <ArrowRight size={16} />
        </button>
      </div>
      <p className="text-xs text-center text-gray-400 mt-3">
        You can always add or change content later from each service’s <b>Train</b> tab.
      </p>
    </div>
  )
}
